import { useEffect, useState } from "react";
import { Button, Card, Dropdown, Flex, Tag, Tooltip, Typography, message } from "antd";
import type { MenuProps } from "antd";
import { BarChartOutlined, InfoCircleOutlined, PlusOutlined, UnorderedListOutlined } from "@ant-design/icons";
import { useGo } from "@refinedev/core";
import { useSelectedStores } from "@/config/context/SelectedItemsContext";
import { axiosInstance } from "@/utilities";
import { API_URL_QUOTES } from "@/services/XCONS_ENV";
import { PresupuestoIcon } from "@/components/icon";
import { formatAmount, type DashboardPeriod } from "../../utils";
import { estadoDeWidget, WidgetPlaceholder } from "../widget-state";
import type { WidgetDataProps } from "../../types";
import { getStatusMap } from "@/utilities/get-status-quotes";
import { useTranslation } from "react-i18next";

const { Text } = Typography;

type Store = { store_id?: number; storeId?: number; code?: string; name: string };
type StatRow = { count: number; amount: number; loading: boolean };
type FunnelEntry = { name: string; count: number; amount: number; bg: string; textColor: string };

// ── SVG funnel — mismo look que SpecialFunnel de la ficha de contacto ─────────

const SvgFunnel = ({ data, height, fmt }: { data: FunnelEntry[]; height: number | string; fmt: (n: number) => string }) => {
  const n = data.length;
  if (n === 0) return null;

  const maxAmount = Math.max(...data.map((d) => d.amount), 1);
  const MIN_RATIO = 0.15;
  const widthRatio = (amount: number) => MIN_RATIO + (amount / maxAmount) * (1 - MIN_RATIO);

  const segments = data.map((entry, i) => {
    const topW = widthRatio(entry.amount);
    const botW = i < n - 1 ? widthRatio(data[i + 1].amount) : Math.max(0.3, topW * 0.5);
    return { ...entry, topW, botW };
  });

  return (
    <div style={{ position: "relative", width: "100%", height }}>
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        style={{ display: "block" }}
      >
        {segments.map((seg, i) => {
          const y0 = (i / n) * 100;
          const y1 = ((i + 1) / n) * 100;
          const x1 = (0.5 - seg.topW / 2) * 100;
          const x2 = (0.5 + seg.topW / 2) * 100;
          const x3 = (0.5 + seg.botW / 2) * 100;
          const x4 = (0.5 - seg.botW / 2) * 100;
          return (
            <polygon
              key={i}
              points={`${x1},${y0} ${x2},${y0} ${x3},${y1} ${x4},${y1}`}
              fill={seg.bg}
            />
          );
        })}
      </svg>

      {segments.map((seg, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            top: `${((i + 0.5) / n) * 100}%`,
            transform: "translateY(-50%)",
            width: "100%",
            textAlign: "center",
            color: seg.textColor,
            pointerEvents: "none",
          }}
        >
          <div style={{ fontSize: 11 }}>{seg.name}</div>
          <div style={{ fontSize: 11 }}>
            <span style={{ color: "rgba(0,0,0,0.45)" }}>{seg.count} ×</span>{" "}
            <strong>{fmt(seg.amount)}</strong>
          </div>
        </div>
      ))}
    </div>
  );
};

const QUOTE_STATES = ["store", "customer", "follow", "buy_on", "bought_assisted", "buy_off"] as const;


const FALLBACK_COLORS = {
  store:          "#13c2c2",
  customer:       "#722ed1",
  follow:         "#fa8c16",
  buy_on:         "rgba(82,196,26,1)",
  bought_assisted:"rgba(56,158,13,1)",
  buy_off:        "rgba(21,116,75,1)",
  comprado:       "#52c41a",
};


const CARD_OVERHEAD = 70; // card header (~40px) + viewToggle (~20px) + borders (~10px)

type Datos = {
  /** Por creación desde D18 (xcons-docs#314); "actividad" queda por compatibilidad con la API anterior. */
  medidoPor?: "creacion" | "actividad";
  etapas: { state: string; count: number; amount: number }[];
  total: { count: number; amount: number };
};

export const KpiChips = ({
  stores,
  period,
  operatorId,
  bare = false,
  totalHeight,
  data,
  estado,
  error,
  nuncaCalculado,
  recargar,
}: {
  stores: Store[];
  period: DashboardPeriod;
  operatorId?: string | null;
  bare?: boolean;
  totalHeight?: number;
} & WidgetDataProps<Datos>) => {
  const { t } = useTranslation();
  const statusMap = getStatusMap(t);

  const stateColors: Record<string, string> = {
    store:          (statusMap.store.style as any).color          ?? FALLBACK_COLORS.store,
    customer:       (statusMap.customer.style as any).color       ?? FALLBACK_COLORS.customer,
    follow:         (statusMap.follow.style as any).color         ?? FALLBACK_COLORS.follow,
    buy_on:         (statusMap.buy_on?.style as any)?.color       ?? FALLBACK_COLORS.buy_on,
    bought_assisted:(statusMap.bought_assisted?.style as any)?.color ?? FALLBACK_COLORS.bought_assisted,
    buy_off:        (statusMap.buy_off?.style as any)?.color      ?? FALLBACK_COLORS.buy_off,
    comprado:       FALLBACK_COLORS.comprado,
  };

  const { selectedStores } = useSelectedStores();
  const go = useGo();
  const [creating, setCreating] = useState(false);
  const [view, setView] = useState<"list" | "chart">("list");
  const sellerCodes = selectedStores.map((s) => s.code);

  // Las seis etapas siempre presentes, aunque el backend no devuelva fila para alguna: una etapa que
  // desaparece del funnel se lee como un bug, no como un cero.
  const cargando = estado === "cargando";
  const porEstado = new Map((data?.etapas ?? []).map((e) => [e.state, e]));
  const stats: Record<string, StatRow> = Object.fromEntries(
    QUOTE_STATES.map((s) => [
      s,
      {
        count: porEstado.get(s)?.count ?? 0,
        amount: porEstado.get(s)?.amount ?? 0,
        loading: cargando,
      },
    ]),
  );
  const status = estadoDeWidget(
    estado,
    error,
    nuncaCalculado,
    (data?.total?.count ?? 0) > 0,
    recargar,
  );

  // Derived: comprado total for the funnel
  const compradoTotal = {
    count:  (stats.buy_on?.count ?? 0) + (stats.bought_assisted?.count ?? 0) + (stats.buy_off?.count ?? 0),
    amount: (stats.buy_on?.amount ?? 0) + (stats.bought_assisted?.amount ?? 0) + (stats.buy_off?.amount ?? 0),
    loading: stats.buy_on?.loading || stats.bought_assisted?.loading || stats.buy_off?.loading,
  };

  const handleCreateQuote = async (storeId: number) => {
    setCreating(true);
    try {
      const { data } = await axiosInstance.post(API_URL_QUOTES, { storeId });
      const newId = data?.quoteId;
      if (!newId) { message.error("No se recibió el id del presupuesto creado"); return; }
      window.open(`/quotes/${newId}`, "_blank");
    } catch (err: any) {
      const apiMsg = err?.response?.data?.message || err?.response?.data?.error;
      message.error(Array.isArray(apiMsg) ? apiMsg.join(", ") : apiMsg || "Error al crear presupuesto");
    } finally {
      setCreating(false);
    }
  };

  const createForStore = (storeCode: string) => {
    const store = stores.find((s) => s.code === storeCode);
    const sid = Number(store?.store_id ?? store?.storeId);
    if (Number.isFinite(sid)) handleCreateQuote(sid);
  };

  const storeMenuProps: MenuProps = {
    items: stores.map((store) => ({
      key: String(store.store_id ?? store.storeId ?? store.code),
      label: store.name,
    })),
    onClick: ({ key }) => {
      const store = stores.find((s) => String(s.store_id ?? s.storeId ?? s.code) === key);
      const sid = Number(store?.store_id ?? store?.storeId);
      if (Number.isFinite(sid)) handleCreateQuote(sid);
    },
  };

  const hasSingleStore = selectedStores.length === 1;
  const plusButton = hasSingleStore ? (
    <Button type="primary" icon={<PlusOutlined />} size="small" loading={creating}
      onClick={() => createForStore(selectedStores[0].code)} />
  ) : (
    <Dropdown menu={storeMenuProps} disabled={creating} trigger={["click"]}>
      <Button type="primary" icon={<PlusOutlined />} size="small" loading={creating} />
    </Dropdown>
  );

  /**
   * El título de la sección es un atajo al listado (QA #273.7).
   *
   * Lleva el filtro de store pero **no** el de etapa: es el atajo de la sección entera. Para una
   * etapa puntual están las filas, que ya navegaban.
   */
  const irAlListado = () => {
    const filters: any[] = [];
    if (sellerCodes.length > 0) {
      filters.push({ field: "quoteSellers.sellerCode", operator: "in", value: sellerCodes });
    }
    go({ to: "/quotes", type: "push", query: { filters } });
  };

  const handleRowClick = (state: string) => {
    const filters: any[] = [
      { field: "quoteSellers.state", operator: "eq", value: state },
    ];
    if (sellerCodes.length > 0) {
      filters.push({ field: "quoteSellers.sellerCode", operator: "in", value: sellerCodes });
    }
    // Sin operatorId no hay un operador puntual en contexto (nada seleccionado, o varios a la
    // vez) — el widget ya está mostrando todo agregado, así que el listado no debe filtrar por
    // participación de nadie en particular.
    if (operatorId) {
      filters.push({ field: "participations.userId", operator: "eq", value: operatorId });
    }
    go({ to: "/quotes", type: "push", query: { filters } });
  };

  const totalCount = Object.values(stats).reduce((s, r) => s + r.count, 0);
  const totalAmount = Object.values(stats).reduce((s, r) => s + r.amount, 0);
  const anyLoading = Object.values(stats).some((r) => r.loading);

  const rows = [
    { key: "store",          tag: { label: "Store",             style: statusMap.store.style } },
    { key: "customer",       tag: { label: "Cliente",           style: statusMap.customer.style } },
    { key: "follow",         tag: { label: "Seguimiento",       style: statusMap.follow.style } },
    { key: "buy_on",         tag: { label: "Comprado On",       style: statusMap.buy_on?.style ?? {} } },
    { key: "bought_assisted",tag: { label: "Comprado asistido", style: statusMap.bought_assisted?.style ?? {} } },
    { key: "buy_off",        tag: { label: "Comprado Off",      style: statusMap.buy_off?.style ?? {} } },
  ];

  const bodyH     = totalHeight ? totalHeight - 38 : undefined;
  const CONTENT_H = bodyH ? bodyH - 26 : 260;
  const CARD_H    = CONTENT_H;

  const rowItems = rows.map((row) => {
    const stat = stats[row.key];
    return (
      <Flex key={row.key} align="center" justify="space-between"
        style={{ padding: bare ? "8px 16px" : "3px 16px", cursor: "pointer" }}
        onClick={() => handleRowClick(row.key)}
        onMouseEnter={(e) => (e.currentTarget.style.background = "#fafafa")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      >
        <Tag style={{ ...row.tag.style, margin: 0, fontWeight: 500 }}>{row.tag.label}</Tag>
        <Flex align="center" gap={10}>
          <Text type="secondary" style={{ fontSize: 13, opacity: stat.loading ? 0.4 : 1 }}>
            {stat.count}
          </Text>
          <Text strong style={{ fontSize: 15, lineHeight: 1, opacity: stat.loading ? 0.4 : 1, minWidth: 48, textAlign: "right" }}>
            {formatAmount(stat.amount)}
          </Text>
        </Flex>
      </Flex>
    );
  });

  // Funnel data — bg/textColor match SpecialFunnel de ficha de contacto
  const funnelData: FunnelEntry[] = [
    { name: "Store",                    count: stats.store.count,    amount: stats.store.amount,    bg: "rgba(230,247,255,1)", textColor: "rgba(8,151,156,1)" },
    { name: "Cliente",                  count: stats.customer.count, amount: stats.customer.amount, bg: "rgba(249,240,255,1)", textColor: "rgba(114,46,209,1)" },
    { name: "Seguimiento",              count: stats.follow.count,   amount: stats.follow.amount,   bg: "rgba(255,247,230,1)", textColor: "rgba(250,140,22,1)" },
    { name: "Comprado On/Off/Asistido", count: compradoTotal.count,  amount: compradoTotal.amount,  bg: "rgba(233,244,239,1)", textColor: "rgba(21,116,75,1)" },
  ];

  const chartView = (
    <div style={{ height: "100%", padding: "4px 8px", boxSizing: "border-box", opacity: (anyLoading || compradoTotal.loading) ? 0.4 : 1 }}>
      <SvgFunnel data={funnelData} height="100%" fmt={formatAmount} />
    </div>
  );

  const viewToggle = (
    <Flex justify="space-between" align="center" style={{ borderTop: "1px solid #f0f0f0", padding: "4px 12px" }}>
      <Flex gap={0} align="center">
        <button
          onClick={() => setView("list")}
          style={{ background: "none", border: "none", cursor: "pointer", padding: "2px 6px", color: view === "list" ? "rgba(0,0,0,0.75)" : "rgba(0,0,0,0.25)", display: "inline-flex", alignItems: "center" }}
        >
          <UnorderedListOutlined style={{ fontSize: 11 }} />
        </button>
        <span style={{ color: "rgba(0,0,0,0.1)" }}>|</span>
        <button
          onClick={() => setView("chart")}
          style={{ background: "none", border: "none", cursor: "pointer", padding: "2px 6px", color: view === "chart" ? "rgba(0,0,0,0.75)" : "rgba(0,0,0,0.25)", display: "inline-flex", alignItems: "center" }}
        >
          <BarChartOutlined style={{ fontSize: 11 }} />
        </button>
      </Flex>
      <Text type="secondary" style={{ fontSize: 12, opacity: anyLoading ? 0.4 : 1 }}>
        {totalCount} × {formatAmount(totalAmount)}
      </Text>
    </Flex>
  );

  if (bare) {
    return (
      <div>
        <Flex align="center" justify="space-between"
          style={{
            background: "rgba(0,0,0,0.02)",
            borderBottom: "1px solid #f0f0f0",
            padding: "0 16px",
            minHeight: 40,
          }}
        >
          <Flex align="center" gap={8}>
            <PresupuestoIcon style={{ width: 16, height: 16, color: "#15744b" }} />
            <span
              style={{ color: "#15744b", fontWeight: 600, cursor: "pointer" }}
              onClick={irAlListado}
            >
              Presupuestos
            </span>
            <Tooltip
              title="Presupuestos creados en el período, por la etapa en la que están hoy, con su cantidad y monto acumulado. Es el mismo total que la Comparativa. Tocá una fila para ir al listado filtrado."
              trigger="click"
              color="#fff"
              overlayInnerStyle={{ color: "rgba(0,0,0,0.85)", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
              overlayStyle={{ maxWidth: 240 }}
            >
              <InfoCircleOutlined style={{ fontSize: 12, color: "rgba(0,0,0,0.3)", cursor: "pointer" }} onClick={(e) => e.stopPropagation()} />
            </Tooltip>
            <Text type="secondary" style={{ fontSize: 13, fontWeight: 400, opacity: anyLoading ? 0.4 : 1 }}>
              {totalCount} x {formatAmount(totalAmount)}
            </Text>
          </Flex>
          {plusButton}
        </Flex>
        {rowItems}
      </div>
    );
  }

  return (
    <Card
      size="small"
      style={totalHeight ? { height: totalHeight } : undefined}
      styles={{ body: { padding: 0, ...(bodyH ? { height: bodyH, display: "flex", flexDirection: "column" } : {}) } }}
      title={
        <Flex align="center" gap={8}>
          <PresupuestoIcon style={{ width: 16, height: 16, color: "#15744b" }} />
          <span
            style={{ color: "#15744b", fontWeight: 600, cursor: "pointer" }}
            onClick={irAlListado}
          >
            Presupuestos
          </span>
          <Tooltip
            title="Presupuestos creados en el período, por la etapa en la que están hoy, con su cantidad y monto acumulado. Es el mismo total que la Comparativa. Tocá una fila para ir al listado filtrado."
            trigger="click"
            color="#fff"
            overlayInnerStyle={{ color: "rgba(0,0,0,0.85)", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
            overlayStyle={{ maxWidth: 240 }}
          >
            <InfoCircleOutlined style={{ fontSize: 12, color: "rgba(0,0,0,0.3)", cursor: "pointer" }} onClick={(e) => e.stopPropagation()} />
          </Tooltip>
        </Flex>
      }
      extra={plusButton}
    >
      <div style={{ ...(bodyH ? { flex: 1 } : { height: CARD_H }), overflow: "hidden" }}>
        {status.tipo !== "ok" ? (
          <WidgetPlaceholder status={status} />
        ) : view === "list" ? (
          rowItems
        ) : (
          chartView
        )}
      </div>
      {viewToggle}
    </Card>
  );
};
